import { MAX_ALLOWED_BTNS } from '@mm/constants/widgets';
import { useEntryCRUDActions } from '@mm/state/actions/EntryCRUDActions';
import { PlatformType } from '@mm/state/model/CampaignsModel';
import { ChatbotChannel } from '@mm/state/model/ChatbotModel';
import { FormEntry } from '@mm/state/model/FormsModel';
import WidgetModel from '@mm/state/model/WidgetModel';
import { StoreState } from '@mm/state/store-interfaces';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import { ItemsContainer } from '../../../editor/features/build/components/widgets/common/sorting';
import { useSorting } from '../../../editor/features/build/components/widgets/common/sortingHook';
import { getWidgetErrors } from '../../../editor/features/build/components/widgets/makeWidget/get-widget-errors';
import FormField from '../../../editor/features/build/requestModels/widgets/FormWidget/FormField';
import { WidgetContainerRefProp } from '../../../editor/features/startBotPromoters/common/StartBotsDialogueEditor/WidgetByType/WidgetByType';
import { showMMSMessageWarning } from '../../../editor/features/startBotPromoters/common/validateSmsRelatedWidgets';
import { OnClickElsewhere } from '../../components/OnClickElsewhere/OnClickElsewhere';
import { useCommonWidgetHandlers } from '../../helpers/widget-helpers/use-common-widget-handlers';
import { BubbleTextInput } from '../common/BubbleTextInput/BubbleTextInput';
import { FacebookPageIcon } from '../common/FacebookPageIcon/FacebookPageIcon';
import { SmsWarningAlert } from '../common/SmsWarningAlert/SmsWarningAlert';
import { useEditMode } from '../common/use-edit-mode';
import { WidgetControls } from '../common/WidetControls/WidgetControls';
import './Form.scss';
import { FormFieldComponent } from './FormField/FormField';

type Props = {
  widget: WidgetModel;
  pageWidgets: WidgetModel[];
  draggableProvided?: DraggableProvided;
  ref?: (instance: React.Ref<HTMLDivElement>) => void;
  showAttributePicker?: boolean;
  showOnlyNameAttributes?: boolean;
  restrictToChannels?: ChatbotChannel[];
  isSmsRelated?: boolean;
  showCountByChannel?: boolean;
  usedInSlideout?: boolean;
  platforms?: PlatformType[];
} & WidgetContainerRefProp;

export const FormWidget = (props: Props) => {
  const {
    widget,
    draggableProvided,
    showAttributePicker = true,
    showOnlyNameAttributes = false,
    restrictToChannels,
    isSmsRelated,
    showCountByChannel,
    usedInSlideout,
    pageWidgets,
    platforms,
  } = props;

  const {
    id: widgetId,
    config: { entries = [], validationMessages, name },
  } = widget;

  const {
    onSortEnd,
    setItemsContainerRef,
    setItemRef,
    getSortedEntries,
    setSortedEntries,
  } = useSorting({ widgetId, entries });

  const { update: updateWidget, updateField } = useCommonWidgetHandlers();

  const entriesErrors = useMemo(
    () => getWidgetErrors(widget, widget.validated.facebook).entries,
    [widget],
  );

  const {
    create: createEntry,
    delete: deleteEntry,
    update: updateEntry,
  } = useEntryCRUDActions();

  const busyWidgets = useSelector(
    (state: StoreState) => state.widget.busyWidgets,
  );
  const [formName, setFormName] = useState(name || '');

  // Note: probably not really needed, could be easily substituted with local, but keeping it this way for now.
  const { editMode: isFormEdit, setEditMode: setIsFormEdit } = useEditMode(
    props,
    'name',
  );

  const updateFormText = (newText: string) => {
    if (newText !== formName) {
      updateField(widget, 'config.name', newText);
    }
    setFormName(newText);
  };

  const onAddField = () => {
    const entries: FormEntry[] = _.get(widget, 'config.entries') || [];

    if (entries) {
      const maxSortOrder = Math.max(
        ...entries.map(entry => entry.sortOrder),
        0,
      );
      createEntry(widgetId, FormField.make(maxSortOrder + 1));
    }
  };

  const onDeleteField = (field: Partial<FormEntry>) => () => {
    deleteEntry(widgetId, field as FormEntry);
  };

  const onUpdateField = (index: number) => async (field: FormEntry) => {
    return updateEntry(widgetId, field);
  };

  const updateFields = (newFields: Partial<FormEntry>[]) => {
    return _.set(_.cloneDeep(widget), 'config.entries', newFields);
  };

  useEffect(() => {
    const entries = _.get(widget, 'config.entries') || [];

    if (entries && entries.length === 0) {
      const updatedWidget = updateFields([...entries, FormField.make(0)]);
      updateWidget(updatedWidget);
    }
  }, []);

  useEffect(() => {
    setFormName(name || '');
  }, [name]);

  const entriesLimitReached = entries
    ? entries.length >= MAX_ALLOWED_BTNS
    : false;

  const disabled = busyWidgets.indexOf(widgetId) !== -1 || entriesLimitReached;

  const validationOptions = validationMessages?.map(v => v.type);

  if (entries) setSortedEntries(entries);

  const sortedEntries = getSortedEntries();

  const handleClickOutside = useCallback(
    () => setIsFormEdit(false),
    [setIsFormEdit],
  );

  const [isVisibleSmsWarningAlert, setIsVisibleSmsWarningAlert] =
    useState(false);
  const showMMSWarning =
    isSmsRelated && showMMSMessageWarning(pageWidgets, widget?.id);

  useEffect(() => {
    setIsVisibleSmsWarningAlert(false);

    if (widget.config.entries && showMMSWarning) {
      const hasWarning = widget.config.entries.some(
        (form: FormEntry) => form.body.length > 160,
      );
      setIsVisibleSmsWarningAlert(hasWarning);
    }
  }, [widget.config.entries, showMMSWarning]);

  return (
    <div className="form-widget__wrapper">
      {isSmsRelated && (
        <SmsWarningAlert
          isVisible={isVisibleSmsWarningAlert}
          setIsVisibleSmsWarningAlert={setIsVisibleSmsWarningAlert}
        />
      )}
      <div className="form-widget">
        <FacebookPageIcon />
        <div className="form__content-column">
          <div className="form">
            <div className="form__name-container">
              Form name
              <OnClickElsewhere
                className="form__name-wrapper"
                handleClick={handleClickOutside}
                id={`form-${widget.id}`}
              >
                <BubbleTextInput
                  className="form__name"
                  showAttributePicker={false}
                  editMode={isFormEdit}
                  onClick={() => setIsFormEdit(true)}
                  value={formName}
                  updateText={updateFormText}
                  showTextLength={false}
                  multiline={false}
                  maxLength={50}
                  emojiPickerColor="#9a9aa2"
                />
              </OnClickElsewhere>
            </div>
            <WidgetControls
              widget={widget}
              draggableProvided={draggableProvided}
              platforms={platforms}
            />
          </div>

          <div>
            <ItemsContainer
              containerRef={setItemsContainerRef}
              className="form-widget__items-container"
              lockAxis="y"
              lockOffset="90%"
              onSortEnd={onSortEnd}
              lockToContainerEdges
              useDragHandle
            >
              {sortedEntries.map((field, index) => (
                <div key={field.id} className="form__field">
                  <FormFieldComponent
                    widget={widget}
                    index={index}
                    field={field}
                    itemRef={setItemRef}
                    validationOptions={validationOptions}
                    onUpdate={onUpdateField(index)}
                    onDelete={onDeleteField(field)}
                    error={entriesErrors[index]}
                    showAttributePicker={showAttributePicker || index !== 0}
                    showOnlyNameAttributes={showOnlyNameAttributes}
                    questionNumber={index + 1}
                    restrictToChannels={restrictToChannels}
                    showCountByChannel={showCountByChannel}
                    usedInSlideout={usedInSlideout}
                  />
                </div>
              ))}
            </ItemsContainer>

            <div
              className="form__add-link"
              onClick={() => !disabled && onAddField()}
            >
              + Add new Form Question
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
